/**
 * Routes Store - Zustand
 *
 * @module core/modules/waste/store
 *
 * @description
 * Gestiona el estado de rutas y viajes del módulo Waste.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { WasteRoute, WasteTrip, TripEvent } from '../types';

/* ===== TYPES ===== */

export interface RoutesState {
  /**
   * Lista de rutas
   */
  routes: WasteRoute[];

  /**
   * Lista de viajes activos
   */
  activeTrips: WasteTrip[];

  /**
   * Estado de carga
   */
  isLoading: boolean;

  /**
   * Error de carga
   */
  error: string | null;
}

export interface RoutesActions {
  /**
   * Cargar rutas
   */
  setRoutes: (routes: WasteRoute[]) => void;

  /**
   * Agregar ruta
   */
  addRoute: (route: WasteRoute) => void;

  /**
   * Actualizar ruta
   */
  updateRoute: (id: string, updates: Partial<WasteRoute>) => void;

  /**
   * Eliminar ruta
   */
  removeRoute: (id: string) => void;

  /**
   * Cargar viajes activos
   */
  setActiveTrips: (trips: WasteTrip[]) => void;

  /**
   * Agregar viaje activo
   */
  addActiveTrip: (trip: WasteTrip) => void;

  /**
   * Actualizar viaje por evento en tiempo real
   */
  handleTripEvent: (event: TripEvent) => void;

  /**
   * Remover viaje completado/cancelado
   */
  removeActiveTrip: (tripId: string) => void;

  /**
   * Estado de carga
   */
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  /**
   * Limpiar store
   */
  clear: () => void;
}

export type RoutesStore = RoutesState & RoutesActions;

/* ===== INITIAL STATE ===== */

const initialState: RoutesState = {
  routes: [],
  activeTrips: [],
  isLoading: false,
  error: null,
};

/* ===== STORE ===== */

export const useRoutesStore = create<RoutesStore>()(
  devtools(
    (set) => ({
      ...initialState,

      setRoutes: (routes) => {
        set({ routes, isLoading: false, error: null });
      },

      addRoute: (route) => {
        set((state) => ({
          routes: [...state.routes, route],
        }));
      },

      updateRoute: (id, updates) => {
        set((state) => ({
          routes: state.routes.map((r) => (r.id === id ? { ...r, ...updates } : r)),
        }));
      },

      removeRoute: (id) => {
        set((state) => ({
          routes: state.routes.filter((r) => r.id !== id),
        }));
      },

      setActiveTrips: (trips) => {
        set({ activeTrips: trips });
      },

      addActiveTrip: (trip) => {
        set((state) => ({
          activeTrips: [...state.activeTrips, trip],
        }));
      },

      handleTripEvent: (event) => {
        set((state) => {
          switch (event.type) {
            case 'started':
              // El viaje ya debería estar agregado via addActiveTrip
              return state;

            case 'completed':
            case 'cancelled':
              return {
                activeTrips: state.activeTrips.filter((t) => t.id !== event.tripId),
              };

            case 'waypoint_reached':
              return {
                activeTrips: state.activeTrips.map((t) =>
                  t.id === event.tripId
                    ? {
                        ...t,
                        containersVisited: event.data?.waypointId
                          ? [...t.containersVisited, event.data.waypointId]
                          : t.containersVisited,
                      }
                    : t
                ),
              };

            default:
              return state;
          }
        });
      },

      removeActiveTrip: (tripId) => {
        set((state) => ({
          activeTrips: state.activeTrips.filter((t) => t.id !== tripId),
        }));
      },

      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error, isLoading: false }),

      clear: () => set(initialState),
    }),
    {
      name: 'waste-routes-store',
    }
  )
);

/* ===== SELECTORS ===== */

export const selectRoutes = (state: RoutesStore): WasteRoute[] => state.routes;
export const selectRouteById = (id: string) => (state: RoutesStore): WasteRoute | undefined =>
  state.routes.find((r) => r.id === id);
export const selectActiveRoutes = (state: RoutesStore): WasteRoute[] =>
  state.routes.filter((r) => r.status === 'active');
export const selectActiveTrips = (state: RoutesStore): WasteTrip[] => state.activeTrips;
export const selectActiveTripById = (id: string) => (state: RoutesStore): WasteTrip | undefined =>
  state.activeTrips.find((t) => t.id === id);
export const selectTripByVehicle = (vehicleId: string) => (state: RoutesStore): WasteTrip | undefined =>
  state.activeTrips.find((t) => t.vehicle.id === vehicleId);
export const selectIsLoading = (state: RoutesStore): boolean => state.isLoading;
export const selectError = (state: RoutesStore): string | null => state.error;
